import type { ComponentType } from 'react'
import { matchPath } from 'react-router-dom'
import {
  Activity,
  Archive,
  ArrowLeftRight,
  BadgePercent,
  Ban,
  BarChart3,
  Bell,
  BookText,
  Bot,
  Boxes,
  Bug,
  Calculator,
  CalendarRange,
  ChartColumnDecreasing,
  ChartLine,
  ClipboardPen,
  CircleDollarSign,
  Component,
  Cookie,
  Database,
  DatabaseZap,
  FileBadge,
  FileText,
  FileUp,
  FileWarning,
  FolderTree,
  Funnel,
  Gauge,
  Globe,
  Grid3x3,
  HardDrive,
  History,
  House,
  LayoutGrid,
  Link2,
  ListChecks,
  Mail,
  MapPinned,
  Megaphone,
  MessageSquareWarning,
  Package,
  PackageMinus,
  PackageSearch,
  Palette,
  PenTool,
  Receipt,
  Plug,
  RefreshCw,
  Route,
  Scale,
  SearchCheck,
  Settings,
  ShieldCheck,
  ShieldOff,
  Shapes,
  ShoppingBag,
  ShoppingCart,
  Siren,
  SlidersHorizontal,
  Split,
  Landmark,
  Lightbulb,
  Store,
  Table,
  Tags,
  Target,
  Telescope,
  Terminal,
  Trophy,
  Undo2,
  Users,
  Wallet,
  Warehouse,
  Workflow,
} from 'lucide-react'
import { accessFor, type RouteAccess } from './access'
import { FEATURES } from '../shared/config/featureFlags'

export interface RouteMetadata {
  label: string
  parent?: string
}

export interface NavNode {
  id: string
  label: string
  icon: ComponentType<{ className?: string }>
  /** Leaf destination. It may include a canonical query parameter. */
  path?: string
  /** The menu has at most one group level. */
  children?: NavNode[]
  /** A known route which must stay out of the menu until its screen is ready. */
  ready?: boolean
  /** Permission required by the corresponding route. Groups derive visibility from children. */
  access?: RouteAccess
  /** Дополнительный beta-gate: даже выданное по ошибке право не открывает пункт. */
  roles?: readonly string[]
}

/**
 * Canonical labels for pages, aliases and direct-entry routes.
 * Navigation leaves obtain their label from this table, so breadcrumbs and
 * menu copy cannot drift independently.
 */
export const PAGE_LABELS: Record<string, RouteMetadata> = {
  '/': { label: 'Главная' },
  '/login': { label: 'Вход' },
  '/forgot-password': { label: 'Восстановление пароля' },
  '/reset-password': { label: 'Новый пароль' },
  '/activate': { label: 'Активация' },

  '/themes-board': { label: 'Фабрика идей', parent: 'Макеты' },
  '/ideas': { label: 'Фабрика идей', parent: 'Макеты' },
  '/pipeline': { label: 'Конвейер', parent: 'Макеты' },
  '/pipeline/source-quality': { label: 'Качество исходников', parent: 'Макеты' },
  '/designs': { label: 'Каталог макетов', parent: 'Макеты' },
  '/designs-v2': { label: 'Каталог макетов', parent: 'Макеты' },
  '/designs-old': { label: 'Каталог макетов (старый)', parent: 'Архив' },
  '/content-funnels': { label: 'Контент-воронки', parent: 'Макеты' },
  '/content-funnel-studio': { label: 'Студия ИИ-контент-воронок', parent: 'Контент-воронки' },
  '/content-funnel-studio/:id': { label: 'Проект студии', parent: 'Студия ИИ-контент-воронок' },
  '/content-funnel-series': { label: 'Контент-воронки', parent: 'Макеты' },
  '/content-funnel-series/new': { label: 'Новая воронка серии', parent: 'Контент-воронки' },
  '/content-funnel-series/slide-sets/new': { label: 'Новый набор слайдов', parent: 'Контент-воронки' },
  '/content-funnel-series/slide-sets/:setId': { label: 'Набор слайдов', parent: 'Контент-воронки' },
  '/content-funnel-series/:runId': { label: 'Воронка серии', parent: 'Контент-воронки' },
  '/generation-templates': { label: 'Контент-воронки', parent: 'Макеты' },
  '/search': { label: 'Поиск' },
  '/designers': { label: 'Дизайнеры', parent: 'Макеты' },
  '/designer-payouts': { label: 'Выплаты', parent: 'Дизайнеры' },
  '/designs-analytics': { label: 'Аналитика продаж', parent: 'Макеты' },

  '/products': { label: 'Каталог товаров', parent: 'Товары и карточки' },
  '/products-v2': { label: 'Каталог товаров', parent: 'Товары и карточки' },
  '/products-old': { label: 'Каталог товаров (старый)', parent: 'Архив' },
  '/products/bulk-edit': { label: 'Массовое редактирование', parent: 'Товары и карточки' },
  '/gingerbreads': { label: 'Каталог пряников', parent: 'Макеты' },
  '/gingerbread-generator': { label: 'Генератор пряников', parent: 'Макеты' },
  '/gingerbread-generator/shapes': { label: 'Формы пряников', parent: 'Макеты' },
  '/gingerbread-generator/new': { label: 'Новое задание', parent: 'Генератор пряников' },
  '/gingerbread-generator/studio/:jobId': { label: 'Студия', parent: 'Генератор пряников' },
  '/gingerbread-generator/jobs/:jobId': { label: 'Задание', parent: 'Генератор пряников' },
  '/gingerbread-generator/legacy': { label: 'Старые наборы', parent: 'Каталог пряников' },
  '/layout-generator': { label: 'Генератор макетов', parent: 'Макеты' },
  '/asset-library': { label: 'Библиотека элементов', parent: 'Макеты' },
  '/layout-generator/new': { label: 'Новое задание', parent: 'Генератор макетов' },
  '/layout-generator/help': { label: 'Справка', parent: 'Генератор макетов' },
  '/layout-generator/studio/:jobId': { label: 'Студия', parent: 'Генератор макетов' },
  '/layout-generator/duplicate': { label: 'Дублирование макетов', parent: 'Генератор макетов' },
  '/layout-generator/duplicate/:id': { label: 'Дублирование макетов', parent: 'Генератор макетов' },
  '/layout-generator/jobs/:id': { label: 'Результаты генерации', parent: 'Генератор макетов' },
  '/layout-generator-v2': { label: 'Генератор макетов v2', parent: 'Макеты' },
  '/layout-generator-v2/new': { label: 'Новое задание', parent: 'Генератор макетов v2' },
  '/layout-generator-v2/studio/:jobId': { label: 'Студия макетов', parent: 'Генератор макетов v2' },
  '/layout-generator-v2/duplicate': { label: 'Дублирование макетов', parent: 'Генератор макетов v2' },
  '/layout-generator-v2/duplicate/:jobId': { label: 'Дублирование макетов', parent: 'Генератор макетов v2' },
  '/layout-generator-v2/jobs/:jobId': { label: 'Результаты генерации', parent: 'Генератор макетов v2' },
  '/wildberries': { label: 'Карточки WB', parent: 'Товары и карточки' },
  '/wildberries-v2': { label: 'Карточки WB', parent: 'Товары и карточки' },
  '/wildberries-old': { label: 'Карточки WB (старый)', parent: 'Архив' },
  '/wildberries/linking': { label: 'Связывание с PIM', parent: 'Товары и карточки' },
  '/wildberries-old/restore-characteristics': {
    label: 'Восстановление характеристик',
    parent: 'Карточки WB (старый)',
  },
  '/ozon': { label: 'Карточки Ozon', parent: 'Товары и карточки' },
  '/ozon-v2': { label: 'Карточки Ozon', parent: 'Товары и карточки' },
  '/ozon-old': { label: 'Карточки Ozon (старый)', parent: 'Архив' },
  '/yandex-market': { label: 'Карточки Я.Маркет', parent: 'Товары и карточки' },
  '/onec-products': { label: 'Товары 1С', parent: 'Товары и карточки' },
  '/site-cards': { label: 'Карточки Сайт', parent: 'Товары и карточки' },
  '/site-products': { label: 'Карточки Сайт', parent: 'Товары и карточки' },
  '/products/wb-cards': { label: 'Обновление карточек WB', parent: 'Товары и карточки' },
  '/marketplace/card-changes': { label: 'Изменения карточек МП', parent: 'Товары и карточки' },
  '/products-seo': { label: 'SEO и генерация', parent: 'Товары и карточки' },
  '/products-seo/rules': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/rules/template/new': { label: 'Новое правило SEO', parent: 'Правила SEO' },
  '/products-seo/rules/template/:id': { label: 'Правило SEO', parent: 'Правила SEO' },
  '/products-seo/cards': { label: 'SEO и генерация', parent: 'Товары и карточки' },
  '/products-seo/generate': { label: 'SEO и генерация', parent: 'Товары и карточки' },
  '/products-seo/analytics': { label: 'SEO и генерация', parent: 'Товары и карточки' },
  '/products-seo/templates': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/templates/new': { label: 'Новое правило SEO', parent: 'Правила SEO' },
  '/products-seo/templates/:id/edit': { label: 'Правило SEO', parent: 'Правила SEO' },
  '/products-seo/library': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/semantics': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/campaigns': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/campaigns/new': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/campaigns/:id': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/settings': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/quality': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo/card/:marketplace/:sellerId/:article': {
    label: 'SEO и генерация',
    parent: 'Товары и карточки',
  },
  '/products-seo/:marketplace/:sellerId/:article': {
    label: 'SEO и генерация',
    parent: 'Товары и карточки',
  },
  '/products-seo-v2': { label: 'SEO и генерация', parent: 'Товары и карточки' },
  '/products-seo-v2/rules': { label: 'Правила SEO', parent: 'SEO и генерация' },
  '/products-seo-v2/rules/template/new': { label: 'Новое правило SEO', parent: 'Правила SEO' },
  '/products-seo-v2/rules/template/:id': { label: 'Правило SEO', parent: 'Правила SEO' },

  '/categories': { label: 'Категории', parent: 'Справочники' },
  '/certificates': { label: 'Сертификаты', parent: 'Справочники' },
  '/attributes': { label: 'Атрибуты', parent: 'Справочники' },
  '/brands': { label: 'Бренды', parent: 'Справочники' },
  '/design-type-matrix': { label: 'Матрица категорий', parent: 'Справочники' },
  '/marketplace-mapping': { label: 'Сопоставление МП', parent: 'Справочники' },
  '/media-mappings': { label: 'Пути изображений', parent: 'Справочники' },
  '/article-mappings': { label: 'Маппинг артикулов', parent: 'Справочники' },
  '/pipeline-rules': { label: 'Правила конвейера', parent: 'Справочники' },
  '/upload-templates': { label: 'Шаблоны заливки', parent: 'Справочники' },

  '/pricing/price-index': { label: 'Индекс цен', parent: 'Цены' },
  '/pricing/unit-economics': { label: 'Юнит-экономика', parent: 'Цены' },
  '/pricing/wb': { label: 'Ценообразование WB', parent: 'Цены' },
  '/pricing/ozon': { label: 'Ценообразование Ozon', parent: 'Цены' },
  '/stocks/stop-list': { label: 'Стоп-лист остатков', parent: 'Остатки' },
  '/stocks/autoupdate': { label: 'Автообновление', parent: 'Остатки' },
  '/stocks/runs': { label: 'Прогоны', parent: 'Остатки' },
  '/stocks/warehouses': { label: 'FBS-склады', parent: 'Остатки' },
  '/stocks/settings': { label: 'Настройки остатков', parent: 'Остатки' },
  '/stocks/banned-brands': { label: 'Запрет брендов', parent: 'Остатки' },
  '/stocks/unliquid': { label: 'Неликвиды', parent: 'Остатки' },
  '/stocks/manual': { label: 'Ручные остатки', parent: 'Остатки' },
  // Волна 6: управление по исключениям — сводка, алерты, РНП, план-факт (раздел «Управление»).
  '/analytics': { label: 'Сводка', parent: 'Управление' },
  '/analytics/alerts': { label: 'Алерты и события', parent: 'Управление' },
  '/analytics/rnp': { label: 'РНП', parent: 'Управление' },
  '/analytics/plan': { label: 'План-факт', parent: 'Управление' },
  '/analytics/sales': { label: 'Продажи', parent: 'Аналитика' },
  '/analytics/funnel': { label: 'Воронка', parent: 'Аналитика' },
  '/analytics/ads': { label: 'Реклама', parent: 'Аналитика' },
  '/analytics/prices': { label: 'Цены и акции', parent: 'Аналитика' },
  '/analytics/feedbacks': { label: 'Отзывы и возвраты', parent: 'Аналитика' },
  '/analytics/geo': { label: 'География', parent: 'Аналитика' },
  '/analytics/returns': { label: 'Возвраты и вывоз', parent: 'Аналитика' },
  '/analytics/seasonal': { label: 'Сезонный календарь', parent: 'Аналитика' },
  '/analytics/designs': { label: 'Макеты и темы', parent: 'Аналитика' },
  '/analytics/abc': { label: 'ABC/XYZ', parent: 'Аналитика' },
  '/analytics/cabinets': { label: 'Кабинеты', parent: 'Аналитика' },
  '/analytics/heroes': { label: 'Товары герои', parent: 'Аналитика' },
  '/analytics/data-quality': { label: 'Качество данных', parent: 'Аналитика' },
  '/analytics/metrics': { label: 'Справочник метрик', parent: 'Аналитика' },
  '/analytics/explorer': { label: 'Исследователь метрик', parent: 'Аналитика' },
  // Оборачиваемость — рядом с поставками: одно место для решений «что лежит и что везти».
  '/analytics/stocks': { label: 'Остатки и оборачиваемость', parent: 'Остатки' },
  // «Деньги» (волна 5) — отдельный раздел «Финансы».
  '/analytics/pnl': { label: 'P&L', parent: 'Финансы' },
  '/analytics/expenses': { label: 'Расходы площадок', parent: 'Финансы' },
  '/analytics/reconciliation': { label: 'Сверка и выплаты', parent: 'Финансы' },
  '/analytics/cost': { label: 'Себестоимость', parent: 'Финансы' },

  '/users': { label: 'Пользователи и права', parent: 'Администрирование' },
  '/settings/access': { label: 'Права ролей', parent: 'Пользователи и права' },
  '/sellers': { label: 'Продавцы', parent: 'Администрирование' },
  '/integrations': { label: 'Интеграции', parent: 'Администрирование' },
  '/integrations/services': { label: 'Сервисы', parent: 'Интеграции' },
  '/settings/marketplace-link': { label: 'Связь с МП', parent: 'Интеграции' },
  '/settings/ai': { label: 'Интеграции ИИ', parent: 'Администрирование' },
  '/settings/storage': { label: 'Хранилище', parent: 'Администрирование' },
  '/settings/ftp': { label: 'FTP', parent: 'Хранилище' },
  '/settings/prod-sync': { label: 'Синхронизация с прод', parent: 'Администрирование' },
  '/settings/makets-storage': { label: 'Хранение макетов', parent: 'Хранилище' },
  '/settings/mail': { label: 'Почта', parent: 'Администрирование' },
  '/moysklad': { label: 'МойСклад', parent: 'Администрирование' },
  '/notifications': { label: 'Уведомления' },
  '/audit-log': { label: 'Журнал действий', parent: 'Администрирование' },
  '/settings/notifications': { label: 'Настройки уведомлений', parent: 'Настройки' },
  '/settings/agent': { label: 'ИИ-агент', parent: 'Настройки' },
  '/settings/agents': { label: 'Подключённые ИИ-агенты', parent: 'Настройки' },
  '/admin/logs': { label: 'Логи системы', parent: 'Администрирование' },
  '/admin/console': { label: 'API-консоль', parent: 'Администрирование' },
  '/settings/backlog': { label: 'Бэклог', parent: 'Администрирование' },
  '/backlog/mine': { label: 'Мои обращения' },
  '/design-system': { label: 'Дизайн-система', parent: 'Администрирование' },
  '/settings/legacy-photogen': { label: 'Legacy PHOTOGEN', parent: 'Администрирование' },
  '/settings': { label: 'Настройки' },
  '/orders': { label: 'Заказы', parent: 'Товары и карточки' },

  '/designer/create': { label: 'Создать макеты', parent: 'ЛК Дизайнера' },
  '/designer/stats': { label: 'Статистика', parent: 'ЛК Дизайнера' },
  '/designer/payouts': { label: 'Выплаты', parent: 'ЛК Дизайнера' },
  '/designer/tariffs': { label: 'Тарифы', parent: 'ЛК Дизайнера' },

  '/seasonal/products': { label: 'Сезонные товары', parent: 'Сезонный календарь' },
  '/seasonal/events': { label: 'События', parent: 'Сезонный календарь' },
  '/supplies/wb': { label: 'Поставки WB', parent: 'Остатки' },
  '/supplies/ozon': { label: 'Поставки Ozon', parent: 'Остатки' },
  '/integrations/wildberries': { label: 'Вайлдберриз', parent: 'Интеграции' },
  '/integrations/ozon': { label: 'Ozon', parent: 'Интеграции' },
  '/integrations/1c': { label: '1С', parent: 'Интеграции' },
  '/integrations/site': { label: 'Сайт', parent: 'Интеграции' },
  '/integrations/licensium': { label: 'Licensium', parent: 'Интеграции' },
  '/integrations/bitrix24': { label: 'Битрикс24', parent: 'Интеграции' },
  '/integrations/ai': { label: 'ИИ', parent: 'Интеграции' },
}

export const routeMetadata: Record<string, RouteMetadata> = Object.fromEntries(
  Object.entries(PAGE_LABELS).map(([path, metadata]) => [path, { ...metadata }]),
)

export function resolveRouteMetadata(pathname: string): RouteMetadata | undefined {
  const exact = routeMetadata[pathname]
  if (exact) return exact
  const pattern = Object.keys(routeMetadata).find((path) => (
    (path.includes(':') || path.includes('*'))
    && matchPath({ path, end: true }, pathname)
  ))
  return pattern ? routeMetadata[pattern] : undefined
}

function pathnameOf(path: string): string {
  const queryIndex = path.indexOf('?')
  return queryIndex === -1 ? path : path.slice(0, queryIndex)
}

function page(
  id: string,
  path: string,
  icon: NavNode['icon'],
  options: Pick<NavNode, 'ready' | 'access' | 'roles'> = {},
): NavNode {
  const pathname = pathnameOf(path)
  const metadata = PAGE_LABELS[pathname]
  if (!metadata) throw new Error(`PAGE_LABELS does not contain ${pathname}`)
  const access = options.access ?? accessFor(pathname)
  if (!access) throw new Error(`ROUTE_ACCESS does not contain ${pathname}`)
  return { id, path, label: metadata.label, icon, ...options, access }
}

function group(id: string, label: string, icon: NavNode['icon'], children: NavNode[]): NavNode {
  return { id, label, icon, children }
}

export const designerCabinet: NavNode = group('designer-cabinet', 'ЛК Дизайнера', PenTool, [
  { ...page('designer-create', '/designer/create', PenTool), ready: false },
  { ...page('designer-stats', '/designer/stats', BarChart3), ready: false },
  { ...page('designer-payouts-page', '/designer/payouts', Wallet), ready: false },
  { ...page('designer-tariffs', '/designer/tariffs', Table), ready: false },
])

const personalNotifications = page('notifications', '/notifications', Bell)

export const designerNavigation: NavNode[] = [designerCabinet]

/**
 * Complete menu registry. Stub leaves remain present with ready:false; the
 * sidebar filters them out. Visible nodes form the five groups from MENU_SPEC.
 */
export const fullNavigation: NavNode[] = [
  page('home', '/', House),
  personalNotifications,
  group('layouts', 'Макеты', Palette, [
    page('ideas', '/ideas', Lightbulb),
    // Скрыты из меню 04.10.2026 (решение владельца): выкладка карточек идёт через «Черновики ПИМ», конвейером не пользуются
    // (одобрение публикации — 3 раза за всё время). Страница по прямой ссылке открывается, как и прежде.
    { ...page('pipeline', '/pipeline', Workflow), ready: false },
    page('designs', '/designs', LayoutGrid),
    page('gingerbreads', '/gingerbreads', Cookie),
    page('content-funnels', '/content-funnels', Funnel),
    page('pipeline-quality', '/pipeline/source-quality', FileWarning),
    { ...page('designers', '/designers?tab=roster', PenTool), ready: false },
    page('gingerbread-generator', '/gingerbread-generator', Cookie),
    page('gingerbread-shapes', '/gingerbread-generator/shapes', Cookie),
    {
      ...page('layout-generator', '/layout-generator', Bot),
      roles: ['admin', 'owner'],
      ready: FEATURES.layoutGenerator,
    },
    // «Генератор макетов v2» скрыт из меню 04.10.2026 (решение владельца): работа идёт в «Генераторе макетов».
    {
      ...page('layout-generator-v2', '/layout-generator-v2', Bot),
      roles: ['admin', 'owner'],
      ready: false,
    },
    { ...page('asset-library', '/asset-library', Boxes), roles: ['admin', 'owner'], ready: FEATURES.layoutGenerator },
  ]),
  group('products-and-cards', 'Товары и карточки', Package, [
    page('products', '/products', Boxes),
    page('wildberries', '/wildberries', Store),
    page('ozon', '/ozon', ShoppingCart),
    page('yandex-market', '/yandex-market', ShoppingBag),
    page('onec-products', '/onec-products', Database),
    page('site-cards', '/site-cards', Globe),
    page('product-linking', '/wildberries/linking', Link2),
    page('products-seo', '/products-seo', SearchCheck),
    page('wb-cards-editor', '/products/wb-cards', Table),
    page('marketplace-card-changes', '/marketplace/card-changes', RefreshCw),
  ]),
  group('references', 'Справочники', FolderTree, [
    page('categories', '/categories', FolderTree),
    page('certificates', '/certificates', FileBadge),
    page('attributes', '/attributes', Tags),
    page('brands', '/brands', ShieldCheck),
    page('design-type-matrix', '/design-type-matrix', Grid3x3),
    page('marketplace-mapping', '/marketplace-mapping', ArrowLeftRight),
    page('media-mappings', '/media-mappings', Route),
    page('article-mappings', '/article-mappings', Table),
    page('pipeline-rules', '/pipeline-rules?tab=categories', SlidersHorizontal),
    page('upload-templates', '/upload-templates', FileUp),
  ]),
  group('prices', 'Цены', CircleDollarSign, [
    page('price-index', '/pricing/price-index', ChartLine),
    page('unit-economics', '/pricing/unit-economics', CircleDollarSign),
    page('pricing-wb', '/pricing/wb', Store),
    page('pricing-ozon', '/pricing/ozon', ShoppingCart),
  ]),
  // Аналитика продаж разложена на три раздела по смыслу и частоте использования (волна 6, 20.10.2026):
  // «Управление» — ежедневный вход: что сломалось сегодня и кто чинит; «Аналитика» — разбор причин от продаж к
  // карточке, рекламе, цене и отзывам, затем ассортимент; «Финансы» — деньги по закрытым периодам.
  group('management', 'Управление', Gauge, [
    page('analytics-summary', '/analytics', Gauge),
    page('analytics-alerts', '/analytics/alerts', Siren),
    page('analytics-rnp', '/analytics/rnp', Activity),
    page('analytics-plan', '/analytics/plan', Target),
  ]),
  group('analytics', 'Аналитика', BarChart3, [
    page('analytics-sales', '/analytics/sales', ChartLine),
    page('analytics-funnel', '/analytics/funnel', Funnel),
    page('analytics-ads', '/analytics/ads', Megaphone),
    page('analytics-prices', '/analytics/prices', BadgePercent),
    page('analytics-feedbacks', '/analytics/feedbacks', MessageSquareWarning),
    page('analytics-geo', '/analytics/geo', MapPinned),
    page('analytics-returns', '/analytics/returns', Undo2),
    page('analytics-seasonal', '/analytics/seasonal', CalendarRange),
    page('analytics-designs', '/analytics/designs', Shapes),
    page('analytics-abc', '/analytics/abc', ChartColumnDecreasing),
    page('analytics-cabinets', '/analytics/cabinets', Split),
    page('analytics-heroes', '/analytics/heroes', Trophy),
    page('analytics-data-quality', '/analytics/data-quality', DatabaseZap),
    page('analytics-metrics', '/analytics/metrics', BookText),
    page('analytics-explorer', '/analytics/explorer', Telescope),
  ]),
  group('finance', 'Финансы', Landmark, [
    page('analytics-pnl', '/analytics/pnl', Landmark),
    page('analytics-expenses', '/analytics/expenses', Receipt),
    page('analytics-reconciliation', '/analytics/reconciliation', Scale),
    page('analytics-cost', '/analytics/cost', Calculator),
  ]),
  group('stocks', 'Остатки', Boxes, [
    page('stocks-stop-list', '/stocks/stop-list', Ban),
    page('stocks-banned-brands', '/stocks/banned-brands', ShieldOff),
    page('stocks-unliquid', '/stocks/unliquid', PackageMinus),
    page('stocks-manual', '/stocks/manual', ClipboardPen),
    page('stocks-autoupdate', '/stocks/autoupdate', RefreshCw),
    page('stocks-runs', '/stocks/runs', ListChecks),
    page('stocks-warehouses', '/stocks/warehouses', Warehouse),
    page('stocks-settings', '/stocks/settings', SlidersHorizontal),
    // Оборачиваемость и планирование FBO-поставок (волны 4 и 6 аналитики). В конце группы: крошка «Остатки»
    // по-прежнему ведёт на стоп-лист — страницы остатков открывают пользователи и без права SalesAnalytics.
    page('analytics-stocks', '/analytics/stocks', PackageSearch),
    page('supplies-wb', '/supplies/wb', Store),
    page('supplies-ozon', '/supplies/ozon', ShoppingCart),
  ]),
  group('administration', 'Администрирование', Settings, [
    page('users', '/users?tab=users', Users),
    page('sellers', '/sellers', Store),
    page('integrations', '/integrations?tab=services', Plug),
    page('settings-ai', '/settings/ai', Bot),
    { ...page('settings-agent', '/settings/agent', Bot), roles: ['admin', 'owner'] },
    page('storage', '/settings/storage?tab=ftp', HardDrive),
    page('settings-prod-sync', '/settings/prod-sync', ArrowLeftRight),
    page('settings-mail', '/settings/mail', Mail),
    page('moysklad', '/moysklad', Warehouse),
    page('audit-log', '/audit-log', History),
    page('admin-logs', '/admin/logs', FileText),
    page('admin-console', '/admin/console', Terminal),
    page('settings-backlog', '/settings/backlog', Bug),
    page('design-system', '/design-system', Component),
    page('settings-legacy-photogen', '/settings/legacy-photogen', Archive),
    page('integration-wildberries', '/integrations/wildberries', Store, { ready: false }),
    page('integration-ozon', '/integrations/ozon', ShoppingCart, { ready: false }),
    page('integration-1c', '/integrations/1c', Plug, { ready: false }),
    page('integration-bitrix24', '/integrations/bitrix24', Plug, { ready: false }),
    page('integration-ai', '/integrations/ai', Bot, { ready: false }),
  ]),
  // Предыдущее поколение каталогов. Живут до тех пор, пока в новых экранах не
  // закроются остатки сценариев; заходят сюда, а не в рабочие группы меню.
  group('archive', 'Архив', Archive, [
    page('designs-old', '/designs-old', LayoutGrid),
    page('products-old', '/products-old', Boxes),
    page('wildberries-old', '/wildberries-old', Store),
    page('ozon-old', '/ozon-old', ShoppingCart),
  ]),
]

/** Paths represented by one menu leaf regardless of their current inner tab. */
/**
 * Адрес раздела меню для крошки-родителя.
 *
 * У групп своей страницы нет, поэтому раздел открывает первую готовую страницу
 * внутри себя. Неготовые страницы пропускаются: крошка не должна приводить
 * на заглушку. Доступ к выбранному маршруту по-прежнему проверяет роутер.
 * Если открывать нечего — вернётся
 * undefined, и крошка останется неактивной подписью.
 */
export function sectionPath(sectionLabel: string): string | undefined {
  const section = fullNavigation.find((node) => node.label === sectionLabel)
  const groupPath = section?.children?.find((child) => child.path && child.ready !== false)?.path
  if (groupPath) return groupPath
  return Object.entries(PAGE_LABELS).find(([path, metadata]) => (
    metadata.label === sectionLabel && !path.includes(':') && !path.includes('*')
  ))?.[0]
}

export const TAB_CONTAINER_PATHS = new Set([
  '/pipeline-rules',
  '/designers',
  '/users',
  '/integrations',
  '/settings/storage',
  '/products-seo',
])

/** Apply readiness and permission visibility recursively, then remove empty groups. */
export function filterNavigation(
  nodes: NavNode[],
  can: (access: RouteAccess) => boolean,
  role?: string | null,
): NavNode[] {
  const result: NavNode[] = []
  for (const node of nodes) {
    if (
      node.ready === false
      || (node.access && !can(node.access))
      || (node.roles && !node.roles.includes(String(role || '').toLowerCase()))
    ) continue
    if (!node.children) {
      result.push(node)
      continue
    }
    const children = filterNavigation(node.children, can, role)
    if (children.length) result.push({ ...node, children })
  }
  return result
}

export interface NavigationLocation {
  pathname: string
  search: string
}

/** Pure active-state predicate shared by Sidebar and its regression tests. */
export function isNavigationPathActive(
  fullPath: string | undefined,
  location: NavigationLocation,
): boolean {
  if (!fullPath) return false
  const queryIndex = fullPath.indexOf('?')
  const path = queryIndex === -1 ? fullPath : fullPath.slice(0, queryIndex)
  const query = queryIndex === -1 ? '' : fullPath.slice(queryIndex + 1)

  if (path === '/') return location.pathname === '/'

  // These base pages have separately addressable descendants in the menu.
  // «Сводка» аналитики (`/analytics`) — вход в раздел, а не родитель остальных страниц `/analytics/*`.
  if ((path === '/pipeline' || path === '/wildberries' || path === '/analytics') && location.pathname !== path) {
    return false
  }
  if (path === '/gingerbread-generator' && location.pathname === '/gingerbread-generator/shapes') {
    return false
  }

  if (location.pathname !== path && !location.pathname.startsWith(`${path}/`)) return false
  if (TAB_CONTAINER_PATHS.has(path) || !query) return true

  const expected = new URLSearchParams(query)
  const actual = new URLSearchParams(location.search)
  for (const [key, value] of expected.entries()) {
    if (actual.get(key) !== value) return false
  }
  return true
}
